/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { addDays, format, isValid, parseISO, startOfDay } from 'date-fns';
import { QUERY_PERSIST_CACHE } from '@airasia-rn/ui';
import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { SearchFormState } from '../types';
import { TripType } from '../types';
import {
  DEFAULT_DEPARTURE_OFFSET_DAYS,
  DEFAULT_RETURN_OFFSET_DAYS,
} from '../configs/constants';
import { getNormalizedSnapListingDates } from '../utils/snapListingContext';

export const SNAP_SEARCH_FORM_STATE_KEY = [
  QUERY_PERSIST_CACHE,
  'snap',
  'searchFormState',
] as const;

type PersistedSnapSearchFormState = Omit<
  SearchFormState,
  'selectedDates' | 'hotelDates'
> & {
  selectedDates: {
    depart: Date | string | undefined;
    return: Date | string | undefined;
  };
  hotelDates: {
    checkIn: Date | string | undefined;
    checkOut: Date | string | undefined;
  };
};

const toDate = (value: Date | string | undefined): Date | undefined => {
  if (!value) {
    return undefined;
  }

  if (value instanceof Date) {
    return value;
  }

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? undefined : parsedDate;
};

const toDateKey = (date?: Date) => {
  if (!date) {
    return '';
  }

  return format(date, 'yyyy-MM-dd');
};

const fromDateKey = (value: string): Date | undefined => {
  if (!value) {
    return undefined;
  }

  const parsedDate = parseISO(value);
  return isValid(parsedDate) ? parsedDate : undefined;
};

const getDefaultSearchFormState = (): SearchFormState => {
  const today = startOfDay(new Date());
  const defaultDepart = addDays(today, DEFAULT_DEPARTURE_OFFSET_DAYS);
  const defaultReturn = addDays(today, DEFAULT_RETURN_OFFSET_DAYS);

  return {
    tripType: TripType.ROUND_TRIP,
    selectedDates: {
      depart: defaultDepart,
      return: defaultReturn,
    },
    hotelDates: {
      checkIn: defaultDepart,
      checkOut: defaultReturn,
    },
    selectDifferentDates: false,
    rooms: [{ id: '1', adults: 2, children: [], infants: 0 }],
    originStation: null,
    destinationStation: null,
  };
};

const normalizeFormState = (
  state: PersistedSnapSearchFormState | undefined,
  fallbackState: SearchFormState,
): SearchFormState => {
  if (!state) {
    return fallbackState;
  }

  const parsedDepart = toDate(state.selectedDates.depart);
  const parsedReturn = toDate(state.selectedDates.return);
  const parsedCheckIn = toDate(state.hotelDates.checkIn);
  const parsedCheckOut = toDate(state.hotelDates.checkOut);
  const today = startOfDay(new Date());
  const isOneWay = state.tripType === TripType.ONE_WAY;
  const isPast = (date: Date | undefined) => !!date && startOfDay(date) < today;

  if (isPast(parsedDepart)) {
    const normalizedDates = getNormalizedSnapListingDates({
      fromDateFlight: toDateKey(parsedDepart),
      fromDateHotel: toDateKey(parsedCheckIn),
      toDateFlight: isOneWay ? '' : toDateKey(parsedReturn),
      toDateHotel: toDateKey(parsedCheckOut),
    });

    return {
      tripType: normalizedDates.toDateFlight
        ? TripType.ROUND_TRIP
        : (state.tripType ?? fallbackState.tripType),
      selectedDates: {
        depart:
          fromDateKey(normalizedDates.fromDateFlight) ??
          fallbackState.selectedDates.depart,
        return:
          fromDateKey(normalizedDates.toDateFlight) ??
          fallbackState.selectedDates.return,
      },
      hotelDates: {
        checkIn:
          fromDateKey(normalizedDates.fromDateHotel) ??
          fallbackState.hotelDates.checkIn,
        checkOut:
          fromDateKey(normalizedDates.toDateHotel) ??
          fallbackState.hotelDates.checkOut,
      },
      selectDifferentDates:
        state.selectDifferentDates ?? fallbackState.selectDifferentDates,
      rooms: state.rooms?.length ? state.rooms : fallbackState.rooms,
      originStation: state.originStation ?? fallbackState.originStation,
      destinationStation:
        state.destinationStation ?? fallbackState.destinationStation,
      fromIsAllAirports: state.fromIsAllAirports,
      toIsAllAirports: state.toIsAllAirports,
    };
  }

  return {
    tripType: state.tripType ?? fallbackState.tripType,
    selectedDates: {
      depart: parsedDepart ?? fallbackState.selectedDates.depart,
      return: isOneWay
        ? undefined
        : isPast(parsedReturn)
          ? fallbackState.selectedDates.return
          : (parsedReturn ?? fallbackState.selectedDates.return),
    },
    hotelDates: {
      checkIn: isPast(parsedCheckIn)
        ? fallbackState.hotelDates.checkIn
        : (parsedCheckIn ?? fallbackState.hotelDates.checkIn),
      checkOut: isPast(parsedCheckOut)
        ? fallbackState.hotelDates.checkOut
        : (parsedCheckOut ?? fallbackState.hotelDates.checkOut),
    },
    selectDifferentDates:
      state.selectDifferentDates ?? fallbackState.selectDifferentDates,
    rooms: state.rooms?.length ? state.rooms : fallbackState.rooms,
    originStation: state.originStation ?? fallbackState.originStation,
    destinationStation:
      state.destinationStation ?? fallbackState.destinationStation,
    fromIsAllAirports: state.fromIsAllAirports,
    toIsAllAirports: state.toIsAllAirports,
  };
};

export const useSnapSearchFormState = (
  initialValues?: SearchFormState,
  options?: { syncInitialValues?: boolean },
) => {
  const queryClient = useQueryClient();
  const defaultFormState = useMemo(
    () => initialValues ?? getDefaultSearchFormState(),
    [initialValues],
  );

  // Ensure query is initialized with staleTime: Infinity and gcTime: Infinity
  // This prevents the data from ever being garbage collected or cleared
  queryClient.setQueryDefaults(SNAP_SEARCH_FORM_STATE_KEY, {
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      queryClient.getQueryCache().subscribe((event) => {
        const queryKey = event.query.queryKey;
        const isSearchFormStateQuery =
          queryKey.length === SNAP_SEARCH_FORM_STATE_KEY.length &&
          queryKey.every(
            (keyPart: unknown, index: number) =>
              keyPart === SNAP_SEARCH_FORM_STATE_KEY[index],
          );

        if (isSearchFormStateQuery) {
          onStoreChange();
        }
      }),
    [queryClient],
  );

  const getSnapshot = useCallback(
    () =>
      queryClient.getQueryData<PersistedSnapSearchFormState>(
        SNAP_SEARCH_FORM_STATE_KEY,
      ),
    [queryClient],
  );

  const persistedState = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getSnapshot,
  );

  useEffect(() => {
    if (!options?.syncInitialValues || !initialValues) {
      return;
    }

    queryClient.setQueryData<SearchFormState>(
      SNAP_SEARCH_FORM_STATE_KEY,
      initialValues,
    );
  }, [initialValues, options?.syncInitialValues, queryClient]);

  const formState = useMemo(
    () => normalizeFormState(persistedState, defaultFormState),
    [persistedState, defaultFormState],
  );

  const updateFormState = useCallback(
    (updates: Partial<SearchFormState>) => {
      queryClient.setQueryData<SearchFormState>(
        SNAP_SEARCH_FORM_STATE_KEY,
        (oldState) => {
          const normalizedState = normalizeFormState(
            oldState,
            defaultFormState,
          );

          return normalizeFormState(
            {
              ...normalizedState,
              ...updates,
            },
            defaultFormState,
          );
        },
      );
    },
    [queryClient, defaultFormState],
  );

  return {
    formState,
    updateFormState,
  };
};
